import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface SwipeableViewProps {
  children: React.ReactNode;
  className?: string;
  routes?: string[]; // Array of routes to navigate between
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number; // Minimum swipe distance
  enabled?: boolean;
}

export function SwipeableView({
  children,
  className,
  routes,
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true,
}: SwipeableViewProps) {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Find current route index
  const currentRouteIndex = routes ? routes.indexOf(location.pathname) : -1;

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return;
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled || touchStart === null) return;
      setTouchEnd(e.targetTouches[0].clientX);
      setIsSwiping(true);
    },
    [enabled, touchStart]
  );

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > threshold;
    const isRightSwipe = distance < -threshold;

    if (isLeftSwipe) {
      if (onSwipeLeft) {
        onSwipeLeft();
      } else if (routes && currentRouteIndex !== -1 && currentRouteIndex < routes.length - 1) {
        // Navigate to next route
        navigate(routes[currentRouteIndex + 1]);
      }
    }

    if (isRightSwipe) {
      if (onSwipeRight) {
        onSwipeRight();
      } else if (routes && currentRouteIndex > 0) {
        // Navigate to previous route
        navigate(routes[currentRouteIndex - 1]);
      }
    }

    setIsSwiping(false);
  }, [
    enabled,
    touchStart,
    touchEnd,
    threshold,
    onSwipeLeft,
    onSwipeRight,
    routes,
    currentRouteIndex,
    navigate,
  ]);

  // Prevent scrolling while swiping horizontally
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const preventScroll = (e: TouchEvent) => {
      if (isSwiping) {
        e.preventDefault();
      }
    };

    container.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      container.removeEventListener('touchmove', preventScroll);
    };
  }, [isSwiping, enabled]);

  return (
    <div
      ref={containerRef}
      className={cn('touch-pan-y', className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children}
    </div>
  );
}

// Hook for programmatic swipe navigation
export function useSwipeNavigation(routes: string[]) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentIndex = routes.indexOf(location.pathname);

  const swipeToNext = useCallback(() => {
    if (currentIndex !== -1 && currentIndex < routes.length - 1) {
      navigate(routes[currentIndex + 1]);
    }
  }, [currentIndex, routes, navigate]);

  const swipeToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigate(routes[currentIndex - 1]);
    }
  }, [currentIndex, routes, navigate]);

  return {
    currentIndex,
    canSwipeLeft: currentIndex !== -1 && currentIndex < routes.length - 1,
    canSwipeRight: currentIndex > 0,
    swipeToNext,
    swipeToPrevious,
  };
}
